import { uploadFileGac } from "../../../service/file-manager";
import { validateFile } from "../../../helpers/validateFile";

const detectFileType = (file) => {
  if (!file) return null;
  const type = file.type || "";
  const name = file.name || "";
  const lowerName = name.toLowerCase();

  if (type.includes("image")) return "image";
  if (type.includes("pdf") || lowerName.endsWith(".pdf")) return "pdf";
  if (
    type.includes("ppt") ||
    lowerName.endsWith(".ppt") ||
    lowerName.endsWith(".pptx")
  )
    return "ppt";
  return "other";
};

const isDocFile = (file) =>
  file.type === "application/pdf" ||
  file.type === "application/vnd.ms-powerpoint" ||
  file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// Extrae el primer File de un valor del formulario. El UploadField entrega
// un File único (maxFiles=1), pero react-hook-form también puede entregar
// un FileList/array (comportamiento del <input type="file">).
const fileFromValue = (value) => {
  if (typeof File !== "undefined" && value instanceof File) return value;
  if (value && typeof value === "object" && typeof value.length === "number") {
    const candidate = Array.from(value)[0];
    if (typeof File !== "undefined" && candidate instanceof File) {
      return candidate;
    }
  }
  return null;
};

// Mensajes de error del servicio de subida, agrupados por código HTTP.
// La API (upload.php) responde con estos códigos y mensajes:
//   400 No se recibió fileToUpload | 404 Archivo no encontrado
//   405 Método no permitido | 413 archivo/solicitud supera el límite
//   415 extensión/MIME no permitido | 500 error interno de escritura
const UPLOAD_ERROR_MESSAGES = {
  400: "No se recibió el archivo (fileToUpload). Verifica e intenta de nuevo.",
  404: "Ruta inexistente: el archivo no fue encontrado en el servidor.",
  405: "Método no permitido por el servidor de subida.",
  413: "El archivo supera el límite permitido por el servidor (20 MB).",
  415: "Extensión o contenido del archivo no permitido (MIME incompatible).",
  500: "No se pudo guardar el archivo en el servidor, intenta de nuevo.",
};

// Obtiene el mensaje de la API (puede venir en data.message, data.error,
// data.mensaje, data.msg o como texto plano) y si no lo encuentra usa el
// mensaje por defecto del código HTTP.
const getUploadErrorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  let rawMessage = null;
  if (data && typeof data === "object") {
    rawMessage = data.message || data.error || data.mensaje || data.msg;
  } else if (typeof data === "string" && data.trim()) {
    rawMessage = data;
  }

  if (typeof rawMessage === "string" && rawMessage.trim()) {
    return rawMessage.trim();
  }

  return (
    UPLOAD_ERROR_MESSAGES[status] ||
    "No se pudo subir el archivo, intenta nuevamente."
  );
};

// Sube un archivo al gestor (upload.php) y devuelve la URL y el nombre
// con que quedó guardado. El servicio espera un FormData con fileToUpload +
// uploadDir y responde con { data: { fileUrl, fileName } } — ver
// src/helpers/fileUtils.js (flujo original).
const uploadFile = async (file, uploadDir) => {
  // Validación local (tipo y tamaño) antes de llegar al servidor.
  const validation = validateFile(file);
  if (validation !== true) {
    throw new Error(validation);
  }

  const form = new FormData();
  form.append("fileToUpload", file);
  form.append("uploadDir", uploadDir);

  let response;
  try {
    response = await uploadFileGac(form);
  } catch (error) {
    throw new Error(getUploadErrorMessage(error));
  }

  const { fileUrl, fileName } = response?.data?.data || {};
  if (!fileUrl) {
    throw new Error(
      "No se pudo obtener la URL del archivo subido (upload.php)",
    );
  }

  return { fileUrl, fileName };
};

export const uploadNewsFiles = async ({ img, preview }) => {
  let imgUrl = null;
  let imgName = null;
  let previewUrl = null;
  let previewName = null;

  if (img) {
    // Los documentos se guardan en "archivos_intranet";
    // las imágenes en "imagenes_intranet".
    const result = await uploadFile(
      img,
      isDocFile(img) ? "archivos_intranet" : "imagenes_intranet",
    );
    imgUrl = result.fileUrl;
    imgName = result.fileName;
  }

  if (preview) {
    const result = await uploadFile(preview, "imagenes_intranet");
    previewUrl = result.fileUrl;
    previewName = result.fileName;
  }

  return {
    imgUrl,
    imgName,
    previewUrl,
    previewName,
  };
};

export { detectFileType, fileFromValue, validateFile };

const fileUtils = {
  uploadNewsFiles,
  detectFileType,
  fileFromValue,
  validateFile,
};

export default fileUtils;